import {
  useEffect,
  useState,
  FormEvent,
} from "react";

import { useAuth } from "./AuthContext";
import { createAuthFetch } from "./apiFetch";

export default function Settings() {
  const {
    user,
    token,
    updateProfile,
    isLoading,
    error,
  } = useAuth();

  const apiFetch = createAuthFetch(token);

  const [currentPassword, setCurrentPassword] =
    useState("");

  const [newUsername, setNewUsername] =
    useState(user?.username ?? "");

  const [newPassword, setNewPassword] =
    useState("");

  const [
    newPasswordConfirm,
    setNewPasswordConfirm,
  ] = useState("");

  const [
    successMessage,
    setSuccessMessage,
  ] = useState<string | null>(null);

  const [
    localError,
    setLocalError,
  ] = useState<string | null>(null);

  // --------------------------------------------------
  // KI-Einstellungen
  // --------------------------------------------------

  const [apiUrl, setApiUrl] =
    useState("");

  const [apiSecret, setApiSecret] =
    useState("");

  const [
    hasStoredSecret,
    setHasStoredSecret,
  ] = useState(false);

  const [models, setModels] =
    useState<string[]>([]);

  const [
    selectedModel,
    setSelectedModel,
  ] = useState("");

  const [
    isLoadingModels,
    setIsLoadingModels,
  ] = useState(false);

  const [
    isTestingAI,
    setIsTestingAI,
  ] = useState(false);

  const [
    isSavingAI,
    setIsSavingAI,
  ] = useState(false);

  const [
    aiError,
    setAiError,
  ] = useState<string | null>(null);

  const [
    aiSuccess,
    setAiSuccess,
  ] = useState<string | null>(null);

  // --------------------------------------------------
  // Gespeicherte KI-Einstellungen laden
  // --------------------------------------------------

  useEffect(() => {
    if (!token) {
      return;
    }

    async function loadAISettings() {
      try {
        const response =
          await apiFetch(
            "/api/ai-settings"
          );

        if (!response.ok) {
          throw new Error(
            "KI-Einstellungen konnten nicht geladen werden."
          );
        }

        const data =
          await response.json();

        setApiUrl(
          data.apiUrl ?? ""
        );

        setSelectedModel(
          data.model ?? ""
        );

        setHasStoredSecret(
          Boolean(data.hasSecret)
        );

        if (data.model) {
          setModels([
            data.model,
          ]);
        }
      } catch (error) {
        console.error(
          "Fehler beim Laden der KI-Einstellungen:",
          error
        );
      }
    }

    loadAISettings();
  }, [token]);

  // --------------------------------------------------
  // Benutzerkonto speichern
  // --------------------------------------------------

  async function handleSubmit(
    e: FormEvent
  ) {
    e.preventDefault();

    setSuccessMessage(null);
    setLocalError(null);

    if (
      newPassword &&
      newPassword !==
        newPasswordConfirm
    ) {
      setLocalError(
        "Neue Passwörter stimmen nicht überein"
      );

      return;
    }

    if (
      !newUsername.trim() &&
      !newPassword
    ) {
      setLocalError(
        "Nichts zu ändern"
      );

      return;
    }

    try {
      await updateProfile({
        currentPassword,

        newUsername:
          newUsername.trim() !==
          user?.username
            ? newUsername.trim()
            : undefined,

        newPassword:
          newPassword ||
          undefined,
      });

      setSuccessMessage(
        "Änderungen gespeichert"
      );

      setCurrentPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
    } catch {
      // Fehler steckt bereits in `error` aus dem Context
    }
  }

  // --------------------------------------------------
  // Modelle laden
  // --------------------------------------------------

  async function handleLoadModels() {
    setAiError(null);
    setAiSuccess(null);

    if (!apiUrl.trim()) {
      setAiError(
        "Bitte eine API-URL eingeben."
      );

      return;
    }

    if (
      !hasStoredSecret &&
      !apiSecret.trim()
    ) {
      setAiError(
        "Bitte ein API-Secret eingeben."
      );

      return;
    }

    try {
      setIsLoadingModels(true);

      const response =
        await apiFetch(
          "/api/ai-settings/models",
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              apiUrl:
                apiUrl.trim(),

              apiSecret:
                apiSecret.trim(),
            }),
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ??
            "Modelle konnten nicht geladen werden."
        );
      }

      const loadedModels:
        string[] =
        Array.isArray(
          data.models
        )
          ? data.models
          : [];

      setModels(
        loadedModels
      );

      if (
        selectedModel &&
        !loadedModels.includes(
          selectedModel
        )
      ) {
        setSelectedModel("");
      }

      if (
        loadedModels.length === 0
      ) {
        setAiError(
          "Die API hat keine verfügbaren Modelle zurückgegeben."
        );

        return;
      }

      setAiSuccess(
        `${loadedModels.length} Modell(e) gefunden.`
      );
    } catch (error) {
      setModels([]);

      setAiError(
        error instanceof Error
          ? error.message
          : "Modelle konnten nicht geladen werden."
      );
    } finally {
      setIsLoadingModels(false);
    }
  }

  // --------------------------------------------------
  // Verbindung testen
  // --------------------------------------------------

  async function handleTestAI() {
    setAiError(null);
    setAiSuccess(null);

    if (!apiUrl.trim()) {
      setAiError(
        "Bitte eine API-URL eingeben."
      );

      return;
    }

    if (
      !hasStoredSecret &&
      !apiSecret.trim()
    ) {
      setAiError(
        "Bitte ein API-Secret eingeben."
      );

      return;
    }

    if (!selectedModel) {
      setAiError(
        "Bitte ein Modell auswählen."
      );

      return;
    }

    try {
      setIsTestingAI(true);

      const response =
        await apiFetch(
          "/api/ai-settings/test",
          {
            method: "POST",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              apiUrl:
                apiUrl.trim(),

              apiSecret:
                apiSecret.trim(),

              model:
                selectedModel,
            }),
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ??
            "Verbindungstest fehlgeschlagen."
        );
      }

      setAiSuccess(
        "Verbindung erfolgreich."
      );
    } catch (error) {
      setAiError(
        error instanceof Error
          ? error.message
          : "Verbindungstest fehlgeschlagen."
      );
    } finally {
      setIsTestingAI(false);
    }
  }

  // --------------------------------------------------
  // KI-Einstellungen speichern
  // --------------------------------------------------

  async function handleSaveAI() {
    setAiError(null);
    setAiSuccess(null);

    if (!apiUrl.trim()) {
      setAiError(
        "Bitte eine API-URL eingeben."
      );

      return;
    }

    if (
      !hasStoredSecret &&
      !apiSecret.trim()
    ) {
      setAiError(
        "Bitte ein API-Secret eingeben."
      );

      return;
    }

    if (!selectedModel) {
      setAiError(
        "Bitte ein Modell auswählen."
      );

      return;
    }

    try {
      setIsSavingAI(true);

      const response =
        await apiFetch(
          "/api/ai-settings",
          {
            method: "PUT",

            headers: {
              "Content-Type":
                "application/json",
            },

            body: JSON.stringify({
              apiUrl:
                apiUrl.trim(),

              apiSecret:
                apiSecret.trim(),

              model:
                selectedModel,
            }),
          }
        );

      const data =
        await response.json();

      if (!response.ok) {
        throw new Error(
          data.error ??
            "KI-Einstellungen konnten nicht gespeichert werden."
        );
      }

      setHasStoredSecret(true);
      setApiSecret("");

      setAiSuccess(
        "KI-Einstellungen gespeichert."
      );
    } catch (error) {
      setAiError(
        error instanceof Error
          ? error.message
          : "KI-Einstellungen konnten nicht gespeichert werden."
      );
    } finally {
      setIsSavingAI(false);
    }
  }

  return (
    <div className="settings-page">
      {/* --------------------------------------------------
          Benutzerkonto
      -------------------------------------------------- */}

      <div className="settings-card">
        <div className="settings-header">
          <h2>Einstellungen</h2>

          <p>
            Ändere deinen Benutzernamen
            oder dein Passwort.
          </p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="settings-form"
        >
          <div className="settings-field">
            <label htmlFor="username">
              Benutzername
            </label>

            <input
              id="username"
              type="text"
              value={newUsername}
              onChange={(e) =>
                setNewUsername(
                  e.target.value
                )
              }
            />
          </div>

          <div className="settings-divider" />

          <div className="settings-field">
            <label htmlFor="new-password">
              Neues Passwort
            </label>

            <span className="settings-hint">
              Leer lassen, wenn du dein
              Passwort nicht ändern
              möchtest.
            </span>

            <input
              id="new-password"
              type="password"
              value={newPassword}
              onChange={(e) =>
                setNewPassword(
                  e.target.value
                )
              }
              autoComplete="new-password"
            />
          </div>

          {newPassword && (
            <div className="settings-field">
              <label htmlFor="new-password-confirm">
                Neues Passwort
                bestätigen
              </label>

              <input
                id="new-password-confirm"
                type="password"
                value={
                  newPasswordConfirm
                }
                onChange={(e) =>
                  setNewPasswordConfirm(
                    e.target.value
                  )
                }
                autoComplete="new-password"
              />
            </div>
          )}

          <div className="settings-divider" />

          <div className="settings-field">
            <label htmlFor="current-password">
              Aktuelles Passwort
            </label>

            <span className="settings-hint">
              Zur Bestätigung deiner
              Änderungen.
            </span>

            <input
              id="current-password"
              type="password"
              value={
                currentPassword
              }
              onChange={(e) =>
                setCurrentPassword(
                  e.target.value
                )
              }
              autoComplete="current-password"
              required
            />
          </div>

          {(localError ||
            error) && (
            <p className="settings-error">
              {localError ??
                error}
            </p>
          )}

          {successMessage && (
            <p className="settings-success">
              {successMessage}
            </p>
          )}

          <div className="settings-actions">
            <button
              className="btn-primary"
              type="submit"
              disabled={isLoading}
            >
              {isLoading
                ? "Speichert..."
                : "Speichern"}
            </button>
          </div>
        </form>
      </div>

      {/* --------------------------------------------------
          KI-Schnittstelle
      -------------------------------------------------- */}

      <div className="settings-card">
        <div className="settings-header">
          <h2>
            KI-Schnittstelle
          </h2>

          <p>
            Konfiguriere eine
            OpenAI-kompatible API für
            die KI-Funktionen des
            Content Pilots.
          </p>
        </div>

        <div className="settings-form">
          <div className="settings-field">
            <label htmlFor="ai-api-url">
              API-URL
            </label>

            <span className="settings-hint">
              Beispiel:
              https://api.openai.com/v1
            </span>

            <input
              id="ai-api-url"
              type="text"
              value={apiUrl}
              onChange={(e) => {
                setApiUrl(
                  e.target.value
                );

                setModels([]);
                setSelectedModel("");
                setAiError(null);
                setAiSuccess(null);
              }}
              placeholder="https://api.openai.com/v1"
            />
          </div>

          <div className="settings-field">
            <label htmlFor="ai-api-secret">
              API-Secret
            </label>

            {hasStoredSecret && (
              <span className="settings-hint">
                Es ist bereits ein
                Secret gespeichert. Gib
                nur dann ein neues
                Secret ein, wenn du es
                ändern möchtest.
              </span>
            )}

            <input
              id="ai-api-secret"
              type="password"
              value={apiSecret}
              onChange={(e) => {
                setApiSecret(
                  e.target.value
                );

                setAiError(null);
                setAiSuccess(null);
              }}
              autoComplete="off"
              placeholder={
                hasStoredSecret
                  ? "Neues Secret eingeben"
                  : "API-Secret"
              }
            />
          </div>

          <div className="settings-actions">
            <button
              type="button"
              className="btn-primary"
              onClick={
                handleLoadModels
              }
              disabled={
                isLoadingModels ||
                !apiUrl.trim() ||
                (
                  !hasStoredSecret &&
                  !apiSecret.trim()
                )
              }
            >
              {isLoadingModels
                ? "Modelle werden geladen..."
                : "Modelle laden"}
            </button>
          </div>

          <div className="settings-divider" />

          <div className="settings-field">
            <label htmlFor="ai-model">
              Modell
            </label>

            <select
              id="ai-model"
              value={
                selectedModel
              }
              onChange={(e) => {
                setSelectedModel(
                  e.target.value
                );

                setAiError(null);
                setAiSuccess(null);
              }}
              disabled={
                models.length === 0
              }
            >
              <option value="">
                Modell auswählen
              </option>

              {models.map(
                (model) => (
                  <option
                    key={model}
                    value={model}
                  >
                    {model}
                  </option>
                )
              )}
            </select>
          </div>

          {aiError && (
            <p className="settings-error">
              {aiError}
            </p>
          )}

          {aiSuccess && (
            <p className="settings-success">
              {aiSuccess}
            </p>
          )}

          <div className="settings-actions">
            <button
              type="button"
              onClick={
                handleTestAI
              }
              disabled={
                isTestingAI ||
                !selectedModel ||
                (
                  !hasStoredSecret &&
                  !apiSecret.trim()
                )
              }
            >
              {isTestingAI
                ? "Verbindung wird getestet..."
                : "Verbindung testen"}
            </button>

            <button
              type="button"
              className="btn-primary"
              onClick={
                handleSaveAI
              }
              disabled={
                isSavingAI ||
                !selectedModel ||
                (
                  !hasStoredSecret &&
                  !apiSecret.trim()
                )
              }
            >
              {isSavingAI
                ? "Speichert..."
                : "KI-Einstellungen speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}